import { Link, Navigate, useLocation, useParams } from "@tanstack/react-router";
import { DASHBOARD_LINKS } from "../../utils/dashboardLinks";
import {
  AMC_QUERY_EXPLORER_BREADCRUMB_SPEC,
  CANNED_QUERIES,
  FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT,
  FEATURED_REPORTS,
  normalizeQueryName,
} from "../../modules/amc/utils/utils";
import { useMemo } from "react";
import BreadcrumbDates from "../../components/core/blocks/BreadcrumbDates";
import { AmcInstanceDetails } from "../../modules/amc/components/instance/AmcInstanceDetails";
import moment from "moment";
import Card from "../../components/core/basic/Card";
import { trellisPalette } from "../../components/custom/analytics/palettes";
import Loading from "../../components/core/blocks/Loading";
import { toTitleCase } from "../../utils/formatText";
import axios from "axios";
import { getURLPrefix } from "../../utils/getUrlPrefix";
import FileSaver from "file-saver";
import { useAMCReportData } from "../../modules/amc/hooks/useAMCReportData";
import { useAMCQueryData } from "../../modules/amc/hooks/useAMCQueryData";
import { useModuleEnabled } from "../../redux/selectors/modules";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { HStack, VStack } from "../../../styled-system/jsx";
import { css, cx } from "../../../styled-system/css";
import GetReportButton from "../../components/core/basic/AMCGetReportButton";
import { buttonRecipe } from "../../components/core/basic/Button";
import { useSimpleCan } from "../../config/casl/casl";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../i18n/useDateFormatter";
import { createAppColumnHelper, useClientTable } from "../../components/table/tableFeatures";
import { customFilterFn } from "../../components/table/filterFns";
import { RT8FilterWidget } from "../../modules/merchandising/components/tables/FilterWidget";
import { RT8Pagination } from "../../components/table/Pagination";
import { Table } from "../../components/table/Table";

const API_URL = getURLPrefix();

export const formatSubscriptionName = (query, t) => {
  const normalizedQueryType = normalizeQueryName(query?.query_type);
  const rawDisplayName = [...CANNED_QUERIES, ...FEATURED_REPORTS].find(
    (cannedQuery) => cannedQuery.name === normalizedQueryType || cannedQuery.name === query?.name,
  )?.display_name;
  let displayName = typeof rawDisplayName === "function" ? t(rawDisplayName) : rawDisplayName;

  if ("frequency" in query) {
    displayName += t(($) => $.amc[": {frequency} Frequency"], { frequency: toTitleCase(query.frequency) });
  }

  if (typeof query.parameters === "object" && "days" in query.parameters) {
    displayName += t(($) => $.amc[", {days} Day Lookback"], { days: query.parameters.days });
  }

  return displayName;
};

const columnHelper = createAppColumnHelper();

const TableComponent = ({ reportData, columns, initialState, isReportDataLoading }) => {
  const table = useClientTable({
    data: reportData,
    columns,
    getRowId: (row) => String(row.id),
    defaultColumn: {
      filterFn: customFilterFn,
    },
    initialState,
    meta: {
      showSkeleton: isReportDataLoading,
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <RT8FilterWidget table={table} />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};

/** @param {{ overrideId?: string | number, fullPage?: boolean }} props */
const AMCReportsListing = ({ overrideId, fullPage = true }) => {
  const { id: queryId } = useParams({ strict: false });
  const pathname = useLocation({ select: (location) => location.pathname });
  const { t } = useTranslation(["amc"]);
  const fmt = useDateFormatter("long");

  const subscriptionId = overrideId ? overrideId : queryId;

  const hasAMCModule = useModuleEnabled("AMC");
  const userCanExportReport = useSimpleCan("export", "AMC");
  const userCanGenerateAmcReports = useSimpleCan("create", "AMC");
  const isPathToPurchaseReportIndex =
    pathname.includes("conversionchart") || overrideId === "pathToPurchaseBaseFeaturedReport";

  const { data: queryData, isLoading } = useAMCQueryData();

  const queryDetails = useMemo(() => {
    if (isPathToPurchaseReportIndex) {
      return FEATURED_REPORTS.find((report) => report.name === "PATH_TO_PURCHASE_GROUPS_INTERNAL");
    }
    const querySubscription =
      queryData?.find((subscribedQuery) => subscribedQuery.id === parseInt(subscriptionId)) ?? {};
    const queryDetails = [...CANNED_QUERIES, ...FEATURED_REPORTS].find(
      (query) => query.name === normalizeQueryName(querySubscription?.query_type),
    );

    return { ...queryDetails, ...querySubscription };
  }, [queryData, subscriptionId, isPathToPurchaseReportIndex]);

  const queryOptions = useMemo(() => {
    const subscribedReportOptions =
      queryData?.map((query) => {
        const displayName = formatSubscriptionName(query, t);
        return { label: displayName, value: query.id };
      }) ?? [];

    const featuredReportOptions =
      FEATURED_REPORTS?.filter((reportType) => FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT.includes(reportType.name)).map(
        (reportType) => ({
          label: typeof reportType.display_name === "function" ? t(reportType.display_name) : reportType.display_name,
          value: reportType.href,
          featured: true,
        }),
      ) ?? [];

    return [...subscribedReportOptions, ...featuredReportOptions];
  }, [queryData, t]);

  const queryType = isPathToPurchaseReportIndex ? "PATH_TO_PURCHASE_GROUPS_INTERNAL" : queryDetails?.query_type;

  const { reportMetadata, isLoading: isReportDataLoading } = useAMCReportData(null, queryType, false, null, true, {
    subscription: queryDetails?.id,
  });

  const reportData = useMemo(() => {
    if (queryDetails.id) {
      return reportMetadata?.filter((report) => report.subscription === queryDetails.id);
    }

    return reportMetadata?.filter((report) => !report.subscription);
  }, [queryDetails.id, reportMetadata]);

  const breadCrumbItems = useMemo(() => {
    const breadCrumbSpec = [
      {
        name: t(($) => $.amc["AMC Dashboard"]),
        href: "/user/amc/dashboard",
        unsorted: true,
        dropdownLinkOptions: DASHBOARD_LINKS,
        dropdownSubtitle: t(($) => $.amc["Reports"]),
        activeOption: t(($) => $.amc["AMC Dashboard"]),
      },
      {
        name: formatSubscriptionName(queryDetails, t),
        href: queryDetails?.featured ? queryDetails?.href : `/user/amc/dashboard/reports/${subscriptionId}`,
        dropdownLinkOptions: queryOptions
          ?.map((query) => {
            const { label, value, featured } = query;

            return {
              name: label,
              href: featured ? value : `/user/amc/dashboard/reports/${value}`,
            };
          })
          .filter((q) => !!q.name),
        searchable: true,
        searchPlaceholderText: t(($) => $.amc["Search subscribed queries..."]),
        dropdownSubtitle: t(($) => $.amc["Subscribed Queries"]),
      },
    ];

    if (!breadCrumbSpec[0].dropdownLinkOptions?.find((o) => o.name === AMC_QUERY_EXPLORER_BREADCRUMB_SPEC.name)) {
      breadCrumbSpec[0].dropdownLinkOptions.push(AMC_QUERY_EXPLORER_BREADCRUMB_SPEC);
    }

    return breadCrumbSpec;
  }, [subscriptionId, queryDetails, queryOptions, t]);

  const handleDownload = async (id, title, fileType) => {
    const url = `${API_URL}/api/exports/${id}/download/`;
    const response = await axios({
      method: "GET",
      url: url,
      responseType: "blob",
    });

    const blobType =
      {
        xlsx: "vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
        csv: "text/csv;charset=utf-8",
      }?.[fileType] ?? "text;charset=utf-8";

    const blob = new Blob([response.data], {
      type: blobType,
    });

    FileSaver.saveAs(blob, `${title}.${fileType}`);
  };

  const columns = useMemo(() => {
    const formatDate = (v) => (v ? fmt.format(moment(v).toDate()) : "-");
    return [
      columnHelper.accessor((row) => row.metadata?.timeWindowStart ?? undefined, {
        id: "start_date",
        header: t(($) => $.amc["Start Date"]),
        size: 180,
        enableResizing: false,
        sortUndefined: "last",
        cell: ({ getValue }) => formatDate(getValue()),
        meta: { columnFilterOptions: { format: "date" } },
      }),
      columnHelper.accessor((row) => row.metadata?.timeWindowEnd ?? undefined, {
        id: "end_date",
        header: t(($) => $.amc["End Date"]),
        size: 180,
        enableResizing: false,
        sortUndefined: "last",
        cell: ({ getValue }) => formatDate(getValue()),
        meta: { columnFilterOptions: { format: "date" } },
      }),
      columnHelper.display({
        id: "frequency",
        header: t(($) => $.amc["Frequency"]),
        size: 110,
        enableResizing: false,
        cell: () => <span>{toTitleCase(queryDetails?.frequency)}</span>,
        meta: { columnSelectorOptions: { description: t(($) => $.amc["Daily: Every Day"]) } },
      }),
      columnHelper.accessor("created_on", {
        header: t(($) => $.amc["Created"]),
        size: 180,
        enableResizing: false,
        cell: ({ getValue }) => formatDate(getValue()),
        meta: { columnFilterOptions: { format: "date" } },
      }),
      columnHelper.display({
        id: "actions",
        header: t(($) => $.amc["Actions"]),
        size: 220,
        enableResizing: false,
        cell: ({ row }) => {
          const {
            export: { id: exportId, name: exportName, file_type: exportFileType },
            id: reportId,
          } = row.original ?? {};
          return (
            <div className="pe-1">
              <Link
                to="/user/amc/dashboard/reports/details/$id"
                params={{ id: String(reportId) }}
                className={cx(
                  buttonRecipe({ variant: "link" }),
                  css({
                    display: "inline-flex",
                    marginRight: "20px",
                    paddingTop: "12px",
                  }),
                )}
              >
                {t(($) => $.amc["View Report"])}
              </Link>
              <span
                style={{
                  cursor: userCanExportReport ? "pointer" : "not-allowed",
                  color: userCanExportReport ? trellisPalette[13] : trellisPalette[10],
                  fontWeight: 600,
                  textDecoration: "underline",
                  opacity: userCanExportReport ? 1 : 0.6,
                }}
                onClick={userCanExportReport ? () => handleDownload(exportId, exportName, exportFileType) : undefined}
                aria-disabled={!userCanExportReport}
              >
                {t(($) => $.amc["Download"])}
              </span>
            </div>
          );
        },
      }),
    ];
  }, [queryDetails, userCanExportReport, t, fmt]);

  const initialState = useMemo(
    () => ({
      sorting: [{ id: "start_date", desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 },
      columnVisibility: { frequency: !isPathToPurchaseReportIndex },
    }),
    [isPathToPurchaseReportIndex],
  );

  const tableComponentProps = {
    reportData,
    columns,
    initialState,
    isReportDataLoading,
  };

  if (!hasAMCModule) {
    return <Navigate to="/user/dashboard" replace />;
  }

  if (!fullPage) {
    return <TableComponent {...tableComponentProps} />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} hideDateSelector={true}>
        <AmcInstanceDetails />
      </BreadcrumbDates>

      {isLoading || isReportDataLoading ?
        <Loading fullPage={true} />
      : <PageContainer withBreadcrumb>
          <VStack
            maxWidth="1300px"
            alignItems={"stretch"}
            width="100%"
            paddingX="xxlarge"
            paddingY="large"
            alignSelf={"center"}
          >
            <h4
              className={css({
                margin: 0,
                fontWeight: "bold",
                textAlign: "center",
              })}
            >
              {queryDetails.parameters?.subscription_title ?
                t(($) => $.amc["{title} Query Reports"], { title: queryDetails.parameters.subscription_title })
              : t(($) => $.amc["{title} Query Reports"], {
                  title:
                    typeof queryDetails.display_name === "function" ?
                      t(queryDetails.display_name)
                    : queryDetails.display_name,
                })
              }
            </h4>

            <Card>
              <TableComponent {...tableComponentProps} />
              {!["PATH_TO_PURCHASE_CAMPAIGNS_INTERNAL", "PATH_TO_PURCHASE_GROUPS_INTERNAL"].includes(
                queryDetails.name,
              ) &&
                userCanGenerateAmcReports && (
                  <HStack justifyContent={"center"} marginTop={"large"}>
                    <GetReportButton queryDetails={queryDetails} />
                  </HStack>
                )}
            </Card>
          </VStack>
        </PageContainer>
      }
    </>
  );
};

export default AMCReportsListing;
